import { Checkbox } from 'antd';
import type { PermissionTree } from '../../../../types/role.types';

interface PermissionGroupNodeProps {
	group: PermissionTree;
	selectedPermissions: Set<string>;
	onTogglePermission: (value: string) => void;
	onToggleGroup: (group: PermissionTree) => void;
	getGroupState: (group: PermissionTree) => { checked: boolean; indeterminate: boolean };
	disabled?: boolean;
	depth?: number;
}

export default function PermissionGroupNode({
	group,
	selectedPermissions,
	onTogglePermission,
	onToggleGroup,
	getGroupState,
	disabled = false,
	depth = 0,
}: PermissionGroupNodeProps) {
	const { checked, indeterminate } = getGroupState(group);

	return (
		<div className={depth === 0 ? 'mb-6' : 'mb-4'}>
			<fieldset className="border border-dashed border-gray-300 rounded-lg p-4">
				<legend className="px-2 border-b-0! w-auto!">
					<Checkbox
						checked={checked}
						indeterminate={indeterminate}
						disabled={disabled}
						onChange={() => onToggleGroup(group)}
					>
						<span className="font-semibold text-gray-800">{group.label}:</span>
					</Checkbox>
				</legend>

				{/* Direct permissions under this group */}
				{group.permissions && group.permissions.length > 0 && (
					<div className="flex flex-wrap gap-x-6 gap-y-3 mb-3">
						{group.permissions.map((perm) => (
							<Checkbox
								key={perm.value}
								checked={selectedPermissions.has(perm.value)}
								disabled={disabled}
								onChange={() => onTogglePermission(perm.value)}
							>
								{perm.label}
							</Checkbox>
						))}
					</div>
				)}

				{/* Nested sub-groups */}
				{group.groups &&
					group.groups.length > 0 &&
					group.groups
						.slice()
						.sort((a, b) => String(a.position).localeCompare(String(b.position)))
						.map((subGroup) => (
							<PermissionGroupNode
								key={subGroup.value}
								group={subGroup}
								selectedPermissions={selectedPermissions}
								onTogglePermission={onTogglePermission}
								onToggleGroup={onToggleGroup}
								getGroupState={getGroupState}
								disabled={disabled}
								depth={depth + 1}
							/>
						))}
			</fieldset>
		</div>
	);
}
